import {
  type CellContext,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  type Room,
  useCreateRoomMutation,
  useGetRooms,
  useUpdateRoomMutation,
} from '~entities/rooms';
import { useDeleteRoomMutation } from '~entities/rooms/api/delete-room';

import { EnrollioTable } from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

const columnHelper = createColumnHelper<Room>();

const columns = [
  columnHelper.accessor('fieldName', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Room Name'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => <div className='px-4 capitalize'>{info.getValue()}</div>,
  }),

  columnHelper.display({
    id: 'Action',
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Action'
        className='justify-end px-4 text-black'
      />
    ),
    cell: (info) => {
      return <RoomActionCell info={info} />;
    },
  }),
];

export function RoomListPage() {
  const [query, setQuery] = useState<string>('');

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isFetching } = useGetRooms({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    ...(query ? { search: query } : {}),
  });

  const tableData = data?.items ?? [];

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const createMutation = useCreateRoomMutation();

  const handleCreateRoom = async () => {
    try {
      await createMutation.mutateAsync({
        fieldName: newRoomName,
      });
      setIsAddDialogOpen(false);
      setNewRoomName('');
      toast.success('Room created successfully');
    } catch (error) {
      toast.error('Failed to create room');
    }
  };

  return (
    <div className='w-full min-w-0 md:w-1/2 lg:w-1/3'>
      <EnrollioTable
        ariaLabel='Rooms'
        totalLabel='Rooms'
        filterHeader={() => {
          return (
            <div className='flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <Input
                placeholder='Search Rooms'
                value={query}
                className='w-full sm:max-w-xs'
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant='secondary'
                    size='sm'
                    className='w-full sm:w-auto'
                  >
                    <PlusIcon />
                    Add New
                  </Button>
                </DialogTrigger>
                <DialogContent className='sm:max-w-[425px]'>
                  <DialogHeader>
                    <DialogTitle>Add New Room</DialogTitle>
                    <DialogDescription>
                      Create a new room by entering the name below.
                    </DialogDescription>
                  </DialogHeader>
                  <div className='grid gap-4 py-4'>
                    <div className='grid grid-cols-4 items-center gap-4'>
                      <Label htmlFor='name' className='text-right'>
                        Room Name
                      </Label>
                      <Input
                        id='name'
                        value={newRoomName}
                        onChange={(event) => {
                          setNewRoomName(event.target.value);
                        }}
                        className='col-span-3'
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant={'outline'}
                      onClick={() => {
                        setIsAddDialogOpen(false);
                        setNewRoomName('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      onClick={handleCreateRoom}
                      disabled={createMutation.isPending || !newRoomName.trim()}
                    >
                      {createMutation.isPending ? (
                        <Loader2Icon className='animate-spin' />
                      ) : (
                        'Create'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          );
        }}
        data={tableData}
        columns={columns}
        rowCount={data?.pagination.total}
        isUpdating={isFetching}
        manualPagination
        pagination={pagination}
        onPaginationChange={setPagination}
      />
    </div>
  );
}

function RoomActionCell({ info }: { info: CellContext<Room, unknown> }) {
  const mutation = useUpdateRoomMutation();
  const deleteMutation = useDeleteRoomMutation();

  const initialRoomName = info.row.original.fieldName;
  const roomId = info.row.original._id;

  const [roomName, setRoomName] = useState(initialRoomName);
  const [isOpen, setIsOpen] = useState(false);

  const handleSave = async () => {
    try {
      await mutation.mutateAsync({
        fieldId: roomId,
        fieldName: roomName,
      });
      setIsOpen(false);
      toast.success('Room updated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update room');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({
        fieldId: roomId,
      });
      toast.success('Room deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete room');
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <div className='flex items-center justify-end gap-2 px-4'>
          <TooltipProvider>
            <Tooltip>
              <DialogTrigger asChild>
                <TooltipTrigger asChild>
                  <Button className='size-6 p-0 text-secondary [&_svg]:size-3'>
                    <PencilIcon />
                  </Button>
                </TooltipTrigger>
              </DialogTrigger>
              <TooltipContent>
                <p className='text-secondary'>Edit Room</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={'destructive'}
                  className='size-6 p-0 [&_svg]:size-3'
                  onClick={handleDelete}
                >
                  <Trash2Icon />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='bg-destructive'>
                <p className='text-destructive-foreground'>Delete Room</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Edit Room</DialogTitle>
            <DialogDescription>
              Make changes to your Room here. Click save when you&apos;re done.
            </DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='name' className='text-right'>
                Room Name
              </Label>
              <Input
                id='name'
                value={roomName}
                onChange={(event) => {
                  setRoomName(event.target.value);
                }}
                className='col-span-3'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant={'outline'}
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              onClick={handleSave}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2Icon className='animate-spin' />
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
